import React, { useContext, useEffect, useState, useRef } from "react";
import { Container, Dropdown, DropdownToggle, DropdownMenu, Badge } from "reactstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/authContext";
import { FiArrowRight, FiClock, FiLogIn, FiLogOut, FiMapPin, FiMenu, FiPhoneCall, FiShield, FiShoppingCart, FiUser, FiUserPlus, FiX, FiTrash2, FiEye } from "react-icons/fi";
import "../../styles/header.css";
import { useCart } from "../../context/cartContext";
import { siteConfig, currencyFormatter, getMediaUrl } from "../../utils/site";
import brandLogo from "../../assets/logo.png";

const navLinks = [
  { path: "/", display: "Home" },
  { path: "/about", display: "Our Story" },
  { path: "/products", display: "Collections" },
  { path: "/course", display: "Roha Course" },
  { path: "/designs", display: "Gallery" },
  { path: "/ai-gallery", display: "AI Studio" },
  { path: "/blogs", display: "Editorial" },
  { path: "/contact", display: "Contact" },
];

const Header = () => {
  const { user, logout, loading } = useContext(AuthContext);
  const { items, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
  const cartDropdownRef = useRef(null);

  // Filter navLinks based on user role
  const filteredNavLinks = navLinks.filter(link => {
    if (link.path === "/generate-image") {
      return user && user.role === "admin";
    }
    return true;
  });

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    closeMenu();
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header 
      className={`roha-header ${isScrolled ? "is-scrolled" : ""}`}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        width: '100%'
      }}
    >
      <div className="roha-header__utility">
        <Container className="roha-header__utility-inner">
          <div className="roha-header__utility-copy">
            <span><FiShield /> Certified gemstones</span>
            <span><FiPhoneCall /> {siteConfig.phones[0]}</span>
            <span><FiMapPin /> {siteConfig.primaryLocation} to worldwide delivery</span>
          </div>
          {/* <Link to="/design-request" className="roha-header__utility-cta">
            Bespoke design request <FiArrowRight />
          </Link> */}
        </Container>
      </div>
      <Container>
        <div className="roha-header__main">
          <Link to="/" className="roha-header__brand text-decoration-none">
            <img src={brandLogo} alt="Roha Jewellery logo" className="roha-header__brand-logo" />
            <span className="roha-header__brand-copy">
              {/* <strong>Roha Jewellery</strong>
              <small>Fine Ethiopian gemstones and jewellery</small> */}
            </span>
          </Link>

          <button
            className="roha-header__toggle d-lg-none"
            onClick={toggleMenu}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </button>

          {/* Mobile auth icons — always visible on mobile next to hamburger */}
          <div className="roha-header__mobile-auth d-flex d-lg-none align-items-center gap-2">
            {!loading && user ? (
              <>
                <Link to="/my-profile" className="text-light header-icon" aria-label="Profile" onClick={closeMenu}>
                  <FiUser size={20} />
                </Link>
                <button
                  onClick={handleLogout}
                  className="roha-header__mobile-logout"
                  aria-label="Logout"
                >
                  <FiLogOut size={20} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="roha-header__mobile-login" onClick={closeMenu}>
                  <FiLogIn size={18} />
                  <span>Login</span>
                </Link>
              </>
            )}
          </div>

          <nav className={`roha-header__nav ${isOpen ? "is-open" : ""}`}>
            {filteredNavLinks.map(({ path, display }, idx) => (
              <NavLink
                to={path}
                key={idx}
                className={({ isActive }) =>
                  `roha-nav-link ${isActive ? "is-active" : ""}`
                }
                onClick={closeMenu}
              >
                {display}
              </NavLink>
            ))}

            {!loading && user && user.role === "admin" && (
              <NavLink
                to="/admin/dashboard"
                className={({ isActive }) =>
                  `roha-nav-link ${isActive ? "is-active" : ""}`
                }
                onClick={closeMenu}
              >
                Dashboard
              </NavLink>
            )}
            {!loading && user && user.role === "delivery" && (
              <NavLink
                to="/delivery/route"
                className={({ isActive }) =>
                  `roha-nav-link ${isActive ? "is-active" : ""}`
                }
                onClick={closeMenu}
              >
                Delivery Route
              </NavLink>
            )}
          </nav>

          <div className="roha-header__actions">
            <div className="roha-header__status d-none d-xl-flex">
              {/* <span><FiClock /> {siteConfig.hours}</span> */}
            </div>
            <Link to="/contact" className="roha-header__cta d-none d-xl-inline-flex">
              Private consultation
            </Link>
            <Dropdown
              isOpen={cartDropdownOpen}
              toggle={() => setCartDropdownOpen(!cartDropdownOpen)}
              className="header-cart-dropdown"
              ref={cartDropdownRef}
            >
              <DropdownToggle
                tag="div"
                className="text-light header-icon position-relative cursor-pointer"
                aria-label="Cart"
                data-toggle="dropdown"
                aria-expanded={cartDropdownOpen}
              >
                <FiShoppingCart size={22} />
                {items.length > 0 && (
                  <span className="cart-badge position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark" style={{fontSize:'0.75rem',minWidth:20}}>
                    {items.reduce((sum, i) => sum + i.quantity, 0)}
                  </span>
                )}
              </DropdownToggle>
              <DropdownMenu end className="roha-mini-cart">
                <div className="roha-mini-cart__header">
                  <h6>Your Cart</h6>
                  <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>
                </div>
                
                {items.length === 0 ? (
                  <div className="roha-mini-cart__empty">
                    <p>Your cart is empty</p>
                    <Link to="/products" className="roha-button roha-button--small" onClick={() => setCartDropdownOpen(false)}>
                      Browse Collection
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="roha-mini-cart__items">
                      {items.slice(0, 3).map((item) => (
                        <div key={item.product.id ?? item.product.uuid} className="roha-mini-cart__item">
                          <img 
                            src={getMediaUrl(item.product.image_url)} 
                            alt={item.product.name}
                            className="roha-mini-cart__item-image"
                          />
                          <div className="roha-mini-cart__item-content">
                            <strong className="roha-mini-cart__item-title">{item.product.name}</strong>
                            <div className="roha-mini-cart__item-details">
                              <span className="roha-mini-cart__item-quantity">Qty: {item.quantity}</span>
                              <span className="roha-mini-cart__item-price">{currencyFormatter.format(Number(item.product.price || 0))}</span>
                            </div>
                          </div>
                          <button
                            className="roha-mini-cart__item-remove"
                            onClick={() => removeItem(item.product.id ?? item.product.uuid)}
                            aria-label="Remove item"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {items.length > 3 && (
                      <div className="roha-mini-cart__more">
                        <p>+{items.length - 3} more items</p>
                      </div>
                    )}
                    
                    <div className="roha-mini-cart__summary">
                      <div className="roha-mini-cart__subtotal">
                        <span>Subtotal</span>
                        <strong>
                          {currencyFormatter.format(
                            items.reduce((sum, item) => sum + Number(item.product.price || 0) * item.quantity, 0)
                          )}
                        </strong>
                      </div>
                    </div>
                    
                    <div className="roha-mini-cart__actions">
                      <Link 
                        to="/cart" 
                        className="roha-button roha-button--ghost w-100"
                        onClick={() => setCartDropdownOpen(false)}
                      >
                        <FiEye className="me-2" /> View Cart
                      </Link>
                      <Link 
                        to="/checkout" 
                        className="roha-button roha-button--solid w-100"
                        onClick={() => setCartDropdownOpen(false)}
                      >
                        Checkout
                      </Link>
                    </div>
                  </>
                )}
              </DropdownMenu>
            </Dropdown>
            {!loading && user ? (
              <>
                <Link to="/my-profile" className="text-light header-icon" aria-label="Profile">
                  <FiUser size={22} />
                </Link>
                <button onClick={handleLogout} className="roha-header__ghost d-none d-lg-inline-flex">
                  <FiLogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="roha-header__ghost d-none d-md-inline-flex" onClick={closeMenu}>
                  <FiLogIn size={16} /> Login
                </Link>
                <Link to="/register" className="roha-header__solid d-none d-md-inline-flex" onClick={closeMenu}>
                  <FiUserPlus size={16} /> Register
                </Link>
              </>
            )}
          </div>
        </div>
        <div className={`roha-header__mobile-panel d-lg-none ${isOpen ? "is-open" : ""}`}>
          <div className="roha-header__mobile-meta">
            <span><FiMapPin /> {siteConfig.primaryLocation}</span>
            <span><FiClock /> {siteConfig.hours}</span>
          </div>
          <div className="roha-header__mobile-actions">
            {!loading && user ? (
              <>
                <Link to="/my-profile" className="roha-header__ghost" onClick={closeMenu}>
                  <FiUser size={16} /> My Profile
                </Link>
                <button onClick={handleLogout} className="roha-header__mobile-logout-full">
                  <FiLogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="roha-header__ghost" onClick={closeMenu}>
                  <FiLogIn size={16} /> Login
                </Link>
                <Link to="/register" className="roha-header__solid" onClick={closeMenu}>
                  <FiUserPlus size={16} /> Sign up
                </Link>
              </>
            )}
            <Link to="/contact" className="roha-header__ghost" onClick={closeMenu}>
              Talk to us
            </Link>
            <Link to="/design-request" className="roha-header__solid" onClick={closeMenu}>
              Custom request
            </Link>
          </div>
        </div>
      </Container>
    </header>
  );
};

export default Header;
